import type { JSONSchema } from '../types';

/**
 * Determines a sensible default value for a JSON Schema.
 *
 * Priority:
 * 1. Uses `schema.default` if specified
 * 2. Falls back to type-appropriate defaults (0 for numbers, '' for strings, false for booleans)
 * 3. For objects, recursively builds default values from properties
 * 4. For arrays, returns empty array (respecting minItems if specified)
 */
export function getDefaultValue(schema: JSONSchema): unknown {
  if (schema.default !== undefined) {
    return schema.default;
  }

  switch (schema.type) {
    case 'number':
    case 'integer':
      return typeof schema.minimum === 'number' ? schema.minimum : 0;
    case 'string':
      if (Array.isArray(schema.enum) && schema.enum.length > 0) {
        return schema.enum[0];
      }
      return '';
    case 'boolean':
      return false;
    case 'object': {
      const properties = schema.properties as Record<string, JSONSchema> | undefined;
      if (!properties) {
        return {};
      }
      const result: Record<string, unknown> = {};
      for (const [key, propSchema] of Object.entries(properties)) {
        result[key] = getDefaultValue(propSchema);
      }
      return result;
    }
    case 'array': {
      const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;
      const itemSchema = schema.items as JSONSchema | undefined;

      // When uniqueItems is required we can't generate multiple valid unique defaults
      // (getDefaultValue produces the same value for the same schema), so bail out
      // early when more than one item is needed and let validation guide the user.
      // A single item always satisfies uniqueness, so minItems: 1 is still handled below.
      if (schema.uniqueItems && minItems > 1) {
        return [];
      }

      // If minItems is specified, pre-populate with that many default items
      if (minItems > 0 && itemSchema) {
        return Array.from({ length: minItems }, () => getDefaultValue(itemSchema));
      }

      return [];
    }
    default:
      return undefined;
  }
}
